/* eslint-disable no-param-reassign */
import { toast } from "react-toastify";
import axios from "axios";
import { confirm } from "../../Layout/Dialog/ConfirmDialog";
import { ProjectViewModel } from "../ViewModel/ProjectViewModel";
import getSpdrsForList from "./LeftSide/InboundTab/GetSpdrsForList";
import getPdsrsForList from "./LeftSide/ReturnTab/GetPdsrsForList";
import { RouteEditTab } from "./RouteEditTab";
import getFtlRoutesWithStates from "./GetFtlRoutesWithStates";
import getLtlPipelinesWithLaunchBatches from "./GetLtlPipelinesWithLaunchBatches";
import { RouteState } from "../ViewModel/RouteState";
import savedButWithoutOverviewFlagDialogOptions from "../../Layout/Dialog/OkDialog";
import { projectUrls } from "../../URL/ApplicationUrls";

function addMissingProperties(project: ProjectViewModel) {
  if (!project) {
    return;
  }

  project.SpdrsOnThisProjectLTL = getSpdrsForList(
    RouteEditTab.Ltl,
    project.SpdrsOnThisProject,
    project.FTLRoutes
  );

  project.PdsrsOnThisProjectLTL = getPdsrsForList(
    RouteEditTab.Ltl,
    project.PdsrsOnThisProject,
    project.FTLRoutes
  );
}

function updateWithSaveProjectResponse(
  project: ProjectViewModel,
  states: Record<string, RouteState>,
  spdrLaunchBatches: Record<string, number | null>,
  pdsrLaunchBatches: Record<string, number | null>,
  modificationDate?: string,
  showSpdrsInOverview?: boolean
): ProjectViewModel {
  const updatedFtlRoutes = getFtlRoutesWithStates(states, project.FTLRoutes ?? []);

  const updatedLtlPipelines = getLtlPipelinesWithLaunchBatches(
    project.ltlPipelines ?? [],
    spdrLaunchBatches,
    pdsrLaunchBatches
  );

  return {
    ...project,
    LastModifiedDateTimeString: modificationDate,
    ShowSpdrsInOverview: showSpdrsInOverview,
    ltlPipelines: updatedLtlPipelines,
    FTLRoutes: updatedFtlRoutes,
  };
}
type SaveProjectProps = {
  project: ProjectViewModel;
  showToast?: boolean;
};
export default async function SaveProject({ project, showToast = true }: SaveProjectProps) {
  addMissingProperties(project);

  if (!project.Name) {
    const message = "Project could not be saved because Project Name is required";
    toast(message, {
      type: "error",
    });
    throw new Error(message);
  }

  async function saveProject() {
    const projectId = project.ProjectId;
    const response = await axios.put(projectUrls.projectWithId(projectId).apiUrl, project);

    if (response.status !== 200) {
      throw new Error("Network response was not ok");
    }

    const json: any = response.data;
    if (!json.Success) {
      let message = json.ErrorMessage;
      const expectedMessage =
        "Project has been changed by an other user. Please reload the page to proceed.";
      if (message === expectedMessage) {
        message = `Unable to save project. ${message} All changes will be lost after reload`;
        const isConfirmed = await confirm(message);
        if (!isConfirmed) {
          throw new Error("User has rejected page reload");
        }

        window.location.reload();
      } else {
        await confirm(message, savedButWithoutOverviewFlagDialogOptions);
        window.location.reload();
        return project;
      }

      throw new Error("Server responded not successfully");
    }

    project = updateWithSaveProjectResponse(
      project,
      json.Data.States ?? {},
      json.Data.SpdrLaunchBatches ?? {},
      json.Data.PdsrLaunchBatches ?? {},
      json.Data.ModificationDate,
      json.Data.ShowSpdrsInOverview
    );

    return project;
  }

  return showToast
    ? toast.promise(saveProject, {
        pending: "Saving project",
        success: "Project saved",
        error: "Project could not be saved",
      })
    : saveProject();
}
